import React, { useEffect, useRef } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { SampleDataBadge } from '../../common';

// Register Chart.js components
ChartJS.register(...registerables, ChartDataLabels);

// Expand icon
const ExpandIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M4 16V12M4 16H8M4 16L9 11M16 4V8M16 4H12M16 4L11 9"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// Color palette for project types
const defaultColors = [
    '#475890', '#5c6daa', '#7483c2', '#a5aee1', '#bec4ec',
];

/**
 * Types Of Projects Chart
 * Donut chart showing project type distribution with blue color palette
 */
const MBFProjectTypesChart = ({ data = [], isSampleData = false, onMaximize, isFullscreen = false }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    // Default data
    const defaultData = [
        { type: 'Course Projects', count: 30 },
        { type: 'Competitions', count: 25 },
        { type: 'Professor Projects', count: 20 },
        { type: 'Self Projects', count: 15 },
        { type: 'Internships', count: 10 },
    ];

    const chartData = data.length > 0 ? data : defaultData;

    // Map data to segments with colors
    const segments = chartData.map((item, index) => ({
        label: item.type || item.name || `Type ${index + 1}`,
        value: item.count || item.percentage || 0,
        color: defaultColors[index % defaultColors.length],
    }));

    // Calculate total for percentage
    const total = segments.reduce((sum, s) => sum + s.value, 0);

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');

            chartInstance.current = new ChartJS(ctx, {
                type: 'doughnut',
                data: {
                    labels: segments.map(s => s.label),
                    datasets: [
                        {
                            data: segments.map(s => s.value),
                            backgroundColor: segments.map(s => s.color),
                            borderColor: 'transparent',
                            borderWidth: 0,
                            borderRadius: 999,
                            spacing: 8,
                            hoverOffset: 0,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '62%',
                    rotation: -0.5 * Math.PI,
                    layout: {
                        padding: 60,
                    },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            titleColor: '#333333',
                            bodyColor: '#333333',
                            borderColor: '#e6e6e6',
                            borderWidth: 1,
                            padding: 10,
                            cornerRadius: 8,
                            callbacks: {
                                label: (context) => {
                                    const value = context.raw;
                                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                    return `${context.label}: ${value} (${percentage}%)`;
                                }
                            }
                        },
                        datalabels: {
                            color: context => segments[context.dataIndex]?.color || '#333',
                            font: {
                                size: 14,
                                weight: 500,
                                family: 'Roboto-Medium, Roboto, sans-serif',
                            },
                            anchor: 'end',
                            align: 'end',
                            offset: 10,
                            formatter: (value, context) => {
                                const percentage = total > 0 ? ((value / total) * 100).toFixed(0) : 0;
                                return (
                                    percentage + '%\n' + context.chart.data.labels[context.dataIndex]
                                );
                            },
                            textAlign: 'center',
                        },
                    },
                },
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [segments, total]);

    // Fullscreen mode - render just the chart without wrapper
    if (isFullscreen) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <canvas ref={chartRef} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                height: '408px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid #e6e6e6',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                        sx={{
                            color: '#333333',
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: '16px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        Types Of Projects
                    </Typography>
                    {isSampleData && <SampleDataBadge absolute={false} />}
                </Box>

                <IconButton
                    size="small"
                    onClick={onMaximize}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: '8px',
                        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.05)',
                        '&:hover': { bgcolor: '#f9f9f9' },
                    }}
                >
                    <ExpandIcon />
                </IconButton>
            </Box>

            {/* Content Area */}
            <Box
                sx={{
                    bgcolor: '#fafafa',
                    padding: '12px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}
            >
                {/* Chart Container */}
                <Box
                    sx={{
                        bgcolor: '#ffffff',
                        borderRadius: '12px',
                        border: '1px solid #e6e6e6',
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px',
                        minHeight: 0,
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                        }}
                    >
                        <canvas ref={chartRef} />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default MBFProjectTypesChart;
